/**
 * 十进制算术（整数缩放 + BigInt），避免 JS IEEE754 在连续加减时的累积误差。
 */

function normalizeDecimalString(input: unknown): string | undefined {
  if (input === null || input === undefined) return undefined;
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return undefined;
    const s = String(input);
    if (!s.includes('e') && !s.includes('E')) return s;
    const expanded = input.toFixed(20);
    return expanded.replace(/\.?0+$/, '');
  }
  if (typeof input === 'string') {
    const s = input.trim();
    if (!s) return undefined;
    if (s === '.') return undefined;
    if (s.startsWith('.')) return `0${s}`;
    if (s.endsWith('.')) return s.slice(0, -1);
    return s;
  }
  return undefined;
}

function decimalPlaces(s: string): number {
  const i = s.indexOf('.');
  return i >= 0 ? s.length - i - 1 : 0;
}

function toScaledBigInt(s: string, scale: number): bigint {
  const negative = s.startsWith('-');
  const raw = negative ? s.slice(1) : s;
  const [intPartRaw, fracRaw = ''] = raw.split('.');
  const intPart = intPartRaw || '0';
  const frac = (fracRaw + '0'.repeat(scale)).slice(0, scale);
  const digits = (intPart + frac).replace(/^0+(?=\d)/, '');
  const bi = BigInt(digits || '0');
  return negative ? -bi : bi;
}

function fromScaledBigInt(v: bigint, scale: number): string {
  const negative = v < 0n;
  const abs = negative ? -v : v;
  let s = abs.toString();
  if (scale > 0) {
    if (s.length <= scale) s = s.padStart(scale + 1, '0');
    const idx = s.length - scale;
    const intPart = s.slice(0, idx);
    const fracPart = s.slice(idx).replace(/0+$/, '');
    s = fracPart ? `${intPart}.${fracPart}` : intPart;
  }
  s = s.replace(/^0+(?=\d)/, '');
  return negative ? `-${s}` : s;
}

export function decimalAdd(a: unknown, b: unknown): string | undefined {
  const sa = normalizeDecimalString(a);
  const sb = normalizeDecimalString(b);
  if (sa === undefined && sb === undefined) return undefined;
  const scale = Math.max(decimalPlaces(sa ?? '0'), decimalPlaces(sb ?? '0'));
  const va = toScaledBigInt(sa ?? '0', scale);
  const vb = toScaledBigInt(sb ?? '0', scale);
  return fromScaledBigInt(va + vb, scale);
}

export function decimalSub(a: unknown, b: unknown): string | undefined {
  const sa = normalizeDecimalString(a);
  const sb = normalizeDecimalString(b);
  if (sa === undefined || sb === undefined) return undefined;
  const scale = Math.max(decimalPlaces(sa), decimalPlaces(sb));
  const va = toScaledBigInt(sa, scale);
  const vb = toScaledBigInt(sb, scale);
  return fromScaledBigInt(va - vb, scale);
}

/** 逐项累加，缺失项按 0；空序列结果为 0 */
export function decimalSum(values: Iterable<unknown>): number {
  let acc = '0';
  for (const v of values) {
    const next = decimalAdd(acc, v);
    acc = next ?? acc;
  }
  const n = Number(acc);
  return Number.isFinite(n) ? n : 0;
}

/** a - b；任一无法规范化时返回 0 */
export function decimalDifference(a: unknown, b: unknown): number {
  const s = decimalSub(a, b);
  if (s === undefined) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}
